// Lays a subject's grants out as a tree following the access store's hierarchy
// (company → platform → resource → child resource), the way the access tree
// shows them. A grant sits on its own object; the objects above it that the
// subject holds nothing on are kept as plain nodes so the path reads whole.
// Objects outside that hierarchy (groups, organizations, providers) become
// top-level nodes of their own.

export interface AccessGrant {
  object: string;
  relation: string;
}

export interface AccessTreeNode {
  id: string;
  type: string;
  label: string;
  grants: AccessGrant[];
  children: AccessTreeNode[];
}

interface Step {
  id: string;
  type: string;
  label: string;
}

// The objects from the top of the hierarchy down to `object` itself.
function pathTo(
  object: string,
  companyName: (id: string) => string | undefined,
): Step[] {
  const colon = object.indexOf(":");
  if (colon < 0) return [{ id: object, type: "other", label: object }];
  const type = object.slice(0, colon);
  const rest = object.slice(colon + 1);
  const parts = rest.split("/");

  if (type === "company" || type === "platform" || type === "resource") {
    const [company, slug, ...segments] = parts;
    const steps: Step[] = [
      {
        id: `company:${company}`,
        type: "company",
        label: companyName(company) ?? company,
      },
    ];
    if (type !== "company" && slug) {
      steps.push({
        id: `platform:${company}/${slug}`,
        type: "platform",
        label: slug,
      });
    }
    if (type === "resource") {
      segments.forEach((segment, i) => {
        steps.push({
          id: `resource:${company}/${slug}/${segments.slice(0, i + 1).join("/")}`,
          type: "resource",
          label: segment,
        });
      });
    }
    return steps;
  }

  return [{ id: object, type, label: type === "group" ? rest : object }];
}

export function buildAccessTree(
  grants: AccessGrant[],
  companyName: (id: string) => string | undefined = () => undefined,
): AccessTreeNode[] {
  const roots: AccessTreeNode[] = [];
  const byId = new Map<string, AccessTreeNode>();

  for (const grant of grants) {
    let siblings = roots;
    let node: AccessTreeNode | undefined;
    for (const step of pathTo(grant.object, companyName)) {
      node = byId.get(step.id);
      if (!node) {
        node = { ...step, grants: [], children: [] };
        byId.set(step.id, node);
        siblings.push(node);
      }
      siblings = node.children;
    }
    node?.grants.push(grant);
  }

  const sort = (nodes: AccessTreeNode[]) => {
    nodes.sort((a, b) => a.label.localeCompare(b.label));
    for (const n of nodes) sort(n.children);
  };
  sort(roots);
  return roots;
}
